import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Lock, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

/**
 * ProPlusGate — wraps a Maker Pro+ feature (Invoices + Contracts). Trials
 * do NOT grant Pro+ access since those features carry real delivery costs
 * (Resend + Stripe Connect). Admins pass through.
 */
export default function ProPlusGate({ feature, pitch, perks, children }) {
  const { user, loading } = useAuth();

  if (loading || user === null) {
    return (
      <div
        className="mx-auto max-w-6xl px-6 py-24 text-center"
        style={{ color: "var(--ink-soft)" }}
        data-testid="pro-plus-gate-loading"
      >
        <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…
      </div>
    );
  }

  const hasAccess = user && (user.plan_tier === "pro_plus" || user.is_admin);
  if (hasAccess) return children;

  return (
    <div className="mx-auto max-w-3xl px-6 py-16" data-testid="pro-plus-gate">
      <div
        className="card-warm rounded-lg overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgba(107,47,186,0.12), rgba(249,115,22,0.10))",
          borderLeft: "3px solid var(--terracotta)",
        }}
      >
        <div className="p-8 sm:p-12">
          <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--terracotta)" }}>
            <Lock className="h-3 w-3" /> Maker Pro+
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3">
            {feature} is part of Maker Pro+.
          </h1>
          {pitch && (
            <p className="mt-4 text-base max-w-xl" style={{ color: "var(--ink-soft)" }}>
              {pitch}
            </p>
          )}
          {perks && perks.length > 0 && (
            <ul className="mt-6 space-y-2.5" data-testid="pro-plus-gate-perks">
              {perks.map((perk, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm">
                  <CheckCircle2
                    className="h-4 w-4 shrink-0 mt-0.5"
                    style={{ color: "var(--terracotta)" }}
                  />
                  <span>{perk}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/pricing?plan=pro_plus"
              data-testid="pro-plus-gate-upgrade-btn"
              className="btn-primary rounded-full px-6 py-3 inline-flex items-center gap-2"
            >
              <Sparkles className="h-4 w-4" />
              {user?.plan === "pro" ? "Upgrade to Pro+" : "See Maker Pro+"}
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/vendor/dashboard"
              className="text-sm underline"
              style={{ color: "var(--ink-soft)" }}
              data-testid="pro-plus-gate-back-btn"
            >
              Back to dashboard
            </Link>
          </div>

          <div className="mt-6 text-xs" style={{ color: "var(--ink-soft)" }}>
            {user?.plan === "pro"
              ? "You're on Maker Pro — the upgrade to Pro+ is prorated. Cancel anytime."
              : "Includes everything in Maker Pro. Cancel anytime."}
          </div>
        </div>
      </div>
    </div>
  );
}
